import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { type RootState } from '../../../store/redux/store/store';
import {
  getMyProfiles,
  getUserProfiles,
  createProfile,
  updateProfile,
  publishProfile,
  unpublishProfile,
  regenerateSlug,
  getPersonas,
  getAchievements,
  addAchievement,
  updateAchievement,
  deleteAchievement,
  getProfileContacts,
  getSocialLinks,
  addSocialLink,
  updateSocialLink,
  deleteSocialLink,
  getProfileSettingsStatus,
  getCompanySettings,
  getFullPublicProfile,
  type PublicProfileView,
  type PublicProfileInput,
  type PublicProfileDetailView,
  type ProfilePersonaView,
  type ProfileAchievementInput,
  type ProfileContactView,
  type ProfileSocialLinkInput,
  type ProfileSettingsStatusView,
  type CompanyProfileSettingsView,
  ProfileStatus,
} from '../../../api/PublicProfile/publicProfileApi';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  type FormData,
  type FormDataValue,
  type Achievement,
  type SocialLink,
  type SnackbarState,
  type Errors,
  type CurrentUser,
  defaultFormData,
} from '../types';
import { copyToClipboard, downloadVCard } from '../utils';

export interface UseProfileConfigurationReturn {
  // State
  formData: FormData;
  profile: PublicProfileView | null;
  personas: ProfilePersonaView[];
  contacts: ProfileContactView[];
  selectedPersona: number | null;
  isLoading: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  errors: Errors;
  snackbar: SnackbarState;
  copiedToast: boolean;
  showShareMenu: boolean;
  showVisibilityAcknowledgment: boolean;
  editingAchievementIndex: number | null;
  activeTab: string;
  newAchievement: Achievement;
  profileUrl: string;
  currentUser: CurrentUser;
  selectedPersonaData: ProfilePersonaView | undefined;
  settingsStatus: ProfileSettingsStatusView | null;
  isHrEditingMode: boolean;
  targetUserName: string | null;
  companyBranding: CompanyProfileSettingsView | null;
  /**
   * The same authoritative payload the public `/p/:slug` page renders from.
   * Lets the LivePreview show the exact persona-merged branding, real user
   * skills, achievements, etc. — fetched whenever a profile slug exists and
   * the profile is publishable. Null for unsaved/draft profiles.
   */
  publicProfileDetail: PublicProfileDetailView | null;

  // Actions
  setActiveTab: (tab: string) => void;
  setShowShareMenu: (show: boolean) => void;
  setShowVisibilityAcknowledgment: (show: boolean) => void;
  setSnackbar: (snackbar: SnackbarState) => void;
  setNewAchievement: React.Dispatch<React.SetStateAction<Achievement>>;
  handleInputChange: (field: keyof FormData, value: FormDataValue) => void;
  handlePersonaChange: (personaId: number) => Promise<void>;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  handlePublicToggle: (value: boolean) => Promise<void>;
  handleAcknowledgePublic: () => Promise<void>;
  handleCopyLink: () => Promise<void>;
  handleRegenerateLink: () => Promise<void>;
  handleDownloadVCard: () => void;
  handleAddAchievement: () => Promise<void>;
  handleEditAchievement: (index: number) => void;
  handleUpdateAchievement: (index: number, field: keyof Achievement, value: string) => void;
  handleSaveAchievementEdit: (index: number) => Promise<void>;
  handleDeleteAchievement: (index: number) => Promise<void>;
  handleMoveAchievement: (index: number, direction: 'up' | 'down') => void;
  handlePreviewAsExternal: () => void;
  validateForm: () => boolean;
  isFieldAllowed: (field: string) => boolean;
  isFieldMandatory: (field: string) => boolean;
  // Social Links
  handleAddSocialLink: (platform: string, url: string) => Promise<void>;
  handleUpdateSocialLink: (index: number, link: SocialLink) => Promise<void>;
  handleDeleteSocialLink: (index: number) => Promise<void>;
  handleMoveSocialLink: (index: number, direction: 'up' | 'down') => void;
}

export function useProfileConfiguration(
  t: (key: string) => string,
  navigate: (path: string) => void,
  targetUserId?: string
): UseProfileConfigurationReturn {
  // HR editing mode - when viewing another user's profile
  const isHrEditingMode = !!targetUserId;
  // Note: targetUserName could be populated by fetching user details if needed
  const targetUserName: string | null = null;
  // Get current user data from Redux store
  const currentUser = useSelector((state: RootState) => ({
    firstName: state.user?.firstName || '',
    lastName: state.user?.lastName || '',
    fullName: state.user?.fullName || '',
    email: state.user?.username || '',
    phone: state.user?.company?.phoneNumber || '',
    imageUrl: state.user?.imageUrl || '',
    title: state.user?.title || '',
    department: state.user?.company?.name || '',
    companyName: state.user?.companyName || state.user?.company?.name || '',
    companyLogo: state.user?.companyLogo || state.user?.company?.logoUrl || '',
  }));

  // State
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showVisibilityAcknowledgment, setShowVisibilityAcknowledgment] = useState(false);
  const [hasAcknowledgedPublic, setHasAcknowledgedPublic] = useState(false);
  const [copiedToast, setCopiedToast] = useState(false);
  const [editingAchievementIndex, setEditingAchievementIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('configuration');
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });

  // API data state
  const [profile, setProfile] = useState<PublicProfileView | null>(null);
  const [profiles, setProfiles] = useState<PublicProfileView[]>([]);
  const [personas, setPersonas] = useState<ProfilePersonaView[]>([]);
  const [contacts, setContacts] = useState<ProfileContactView[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<number | null>(null);
  const [settingsStatus, setSettingsStatus] = useState<ProfileSettingsStatusView | null>(null);
  const [companyBranding, setCompanyBranding] = useState<CompanyProfileSettingsView | null>(null);
  const [publicProfileDetail, setPublicProfileDetail] = useState<PublicProfileDetailView | null>(null);

  // Form data state
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [errors, setErrors] = useState<Errors>({});
  const [newAchievement, setNewAchievement] = useState<Achievement>({
    title: '',
    date: '',
    description: '',
    displayOrder: 0
  });

  // Computed values
  const profileUrl = formData?.publicSlug ? `${window.location?.origin}/p/${formData?.publicSlug}` : '';
  const selectedPersonaData = personas.find(p => p.id === selectedPersona);

  // Load a specific profile into the form + fetch its achievements/social links/contacts.
  const hydrateForProfile = useCallback(async (existingProfile: PublicProfileView) => {
    setProfile(existingProfile);
    setFormData(prev => ({
      ...prev,
      publicSlug: existingProfile.slug || '',
      bio: existingProfile.bio || '',
      jobTitle: existingProfile.customJobTitle || '',
      location: existingProfile.customLocation || '',
      companyNameOverride: existingProfile.customCompanyName || '',
      emailOverride: existingProfile.customEmail || '',
      phoneOverride: existingProfile.customPhone || '',
      showEmail: existingProfile.showEmail,
      showPhone: existingProfile.showPhone,
      showSkills: existingProfile.showSkills,
      showAchievements: existingProfile.showAchievements,
      colorScheme: existingProfile.colorScheme || 'blue',
      profileLanguage: existingProfile.language || 'en',
      isPublic: existingProfile.isPublic,
      profileStatus: existingProfile.status?.toLowerCase() || 'draft',
      achievements: [],
      socialLinks: [],
    }));

    const [achievementsResult, contactsResult, socialLinksResult] = await Promise.all([
      getAchievements(existingProfile.id),
      getProfileContacts(existingProfile.id),
      getSocialLinks(existingProfile.id)
    ]);

    if (achievementsResult.status === 200 && achievementsResult.payload) {
      setFormData(prev => ({
        ...prev,
        achievements: achievementsResult.payload!.map(a => ({
          id: a.id,
          title: a.title,
          date: a.date || '',
          description: a.description || '',
          displayOrder: a.displayOrder
        }))
      }));
    }
    if (contactsResult.status === 200 && contactsResult.payload) {
      setContacts(contactsResult.payload);
    } else {
      setContacts([]);
    }
    if (socialLinksResult.status === 200 && socialLinksResult.payload) {
      setFormData(prev => ({
        ...prev,
        socialLinks: socialLinksResult.payload!.map(s => ({
          id: s.id,
          platform: s.platform,
          url: s.url,
          iconName: s.iconName,
          displayOrder: s.displayOrder
        }))
      }));
    }
  }, []);

  // Reset the per-profile portion of the form when switching to a persona
  // that has no existing profile yet (so the user can create one).
  const resetFormForNewProfile = useCallback((persona: ProfilePersonaView) => {
    setProfile(null);
    setContacts([]);
    setFormData(prev => ({
      ...prev,
      publicSlug: '',
      bio: '',
      jobTitle: '',
      location: '',
      companyNameOverride: '',
      emailOverride: '',
      phoneOverride: '',
      showEmail: true,
      showPhone: true,
      showSkills: true,
      showAchievements: true,
      colorScheme: persona.defaultColorScheme || 'blue',
      profileLanguage: prev.profileLanguage || 'en',
      isPublic: false,
      profileStatus: 'draft',
      achievements: [],
      socialLinks: [],
    }));
  }, []);

  // Fetch profile data on mount
  const fetchProfileData = useCallback(async () => {
    setIsLoading(true);
    try {
      // When HR is editing another user's profile, use getUserProfiles
      const profilesPromise = targetUserId
        ? getUserProfiles(targetUserId)
        : getMyProfiles();

      const [personasResult, profilesResult, settingsResult, brandingResult] = await Promise.all([
        getPersonas(),
        profilesPromise,
        getProfileSettingsStatus(),
        getCompanySettings().catch(() => ({ status: 500, payload: null })) // Non-HR users may not have access
      ]);

      // Set settings status
      if (settingsResult.status === 200 && settingsResult.payload) {
        setSettingsStatus(settingsResult.payload);
      }

      // Set company branding
      if (brandingResult.status === 200 && brandingResult.payload) {
        setCompanyBranding(brandingResult.payload);
      }

      const loadedProfiles = profilesResult.status === 200 && profilesResult.payload
        ? profilesResult.payload
        : [];
      setProfiles(loadedProfiles);

      if (personasResult.status === 200 && personasResult.payload) {
        setPersonas(personasResult.payload);

        // Pick the starting persona: prefer one the user already has a profile for,
        // otherwise the default persona, otherwise the first.
        const defaultPersona = personasResult.payload.find(p => p.isDefault) || personasResult.payload[0];
        const initialProfile = loadedProfiles[0];
        const startingPersona = initialProfile
          ? personasResult.payload.find(p => p.id === initialProfile.personaId) ?? defaultPersona
          : defaultPersona;

        if (startingPersona) {
          setSelectedPersona(startingPersona.id);
          setFormData(prev => ({
            ...prev,
            hrPolicies: {
              ...prev.hrPolicies,
              requiresApproval: startingPersona.requiresApproval,
              canRegenerateLink: startingPersona.allowSlugRegeneration
            },
            canRegenerateLink: startingPersona.allowSlugRegeneration,
            colorScheme: startingPersona.defaultColorScheme || 'blue'
          }));

          const matchingProfile = loadedProfiles.find(p => p.personaId === startingPersona.id);
          if (matchingProfile) {
            await hydrateForProfile(matchingProfile);
          } else {
            resetFormForNewProfile(startingPersona);
          }
        }
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to load profile data'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [t, targetUserId, hydrateForProfile, resetFormForNewProfile]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  // Whenever the loaded profile slug changes, pull the full public-profile
  // payload so the LivePreview can render from the exact same data shape the
  // public `/p/:slug` page receives — persona-merged branding, real
  // UserSkills, achievements, etc. The endpoint is anonymous and only
  // returns published profiles, so silently skip when the profile is still
  // a draft.
  useEffect(() => {
    const slug = profile?.slug;
    if (!slug) {
      setPublicProfileDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await getFullPublicProfile(slug);
      if (cancelled) return;
      if (res.status === 200 && res.payload) {
        setPublicProfileDetail(res.payload);
      } else {
        setPublicProfileDetail(null);
      }
    })().catch(() => {
      if (!cancelled) setPublicProfileDetail(null);
    });
    return () => { cancelled = true; };
  }, [profile?.slug, profile?.isPublic, profile?.status]);

  // Handlers
  const handleInputChange = useCallback((field: keyof FormData, value: FormDataValue) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);

    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handlePersonaChange = useCallback(async (personaId: number) => {
    setSelectedPersona(personaId);
    const persona = personas.find(p => p.id === personaId);
    if (!persona) return;

    setFormData(prev => ({
      ...prev,
      hrPolicies: {
        ...prev.hrPolicies,
        requiresApproval: persona.requiresApproval,
        canRegenerateLink: persona.allowSlugRegeneration
      },
      canRegenerateLink: persona.allowSlugRegeneration
    }));

    // Load the existing profile for this persona, or reset the form for a new one.
    const matchingProfile = profiles.find(p => p.personaId === personaId);
    if (matchingProfile) {
      await hydrateForProfile(matchingProfile);
    } else {
      resetFormForNewProfile(persona);
    }
    setHasUnsavedChanges(false);
  }, [personas, profiles, hydrateForProfile, resetFormForNewProfile]);

  const handlePublishToggle = useCallback(async (makePublic: boolean) => {
    if (!profile) {
      setSnackbar({ open: true, message: t('Please save your profile first'), severity: 'info' });
      return;
    }

    try {
      const result = makePublic
        ? await publishProfile(profile.id)
        : await unpublishProfile(profile.id);

      if (result.status === 200 && result.payload) {
        const saved = result.payload;
        setProfile(saved);
        setProfiles(prev => prev.map(p => p.id === saved.id ? saved : p));
        setFormData(prev => ({
          ...prev,
          isPublic: saved.isPublic,
          profileStatus: saved.status?.toLowerCase() || 'draft'
        }));
        setSnackbar({
          open: true,
          message: makePublic
            ? (saved.status === ProfileStatus.PendingApproval
                ? t('Profile submitted for HR approval')
                : t('Profile published successfully'))
            : t('Profile unpublished'),
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to update profile visibility'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to update profile visibility'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }, [profile, t]);

  const handlePublicToggle = useCallback(async (value: boolean) => {
    // Check if employee is allowed to toggle visibility (FR-006)
    if (!value && settingsStatus && !settingsStatus.allowEmployeeToggle) {
      setSnackbar({
        open: true,
        message: t('You are not allowed to change your profile visibility. Contact HR.'),
        severity: 'error'
      });
      return;
    }

    if (value && !hasAcknowledgedPublic) {
      setShowVisibilityAcknowledgment(true);
    } else {
      await handlePublishToggle(value);
    }
  }, [hasAcknowledgedPublic, handlePublishToggle, settingsStatus, t]);

  const handleAcknowledgePublic = useCallback(async () => {
    setHasAcknowledgedPublic(true);
    setShowVisibilityAcknowledgment(false);
    await handlePublishToggle(true);
  }, [handlePublishToggle]);

  const handleCopyLink = useCallback(async () => {
    if (!profileUrl) return;
    const success = await copyToClipboard(profileUrl);
    if (success) {
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 2000);
    }
  }, [profileUrl]);

  const handleRegenerateLink = useCallback(async () => {
    if (!formData?.hrPolicies?.canRegenerateLink) {
      setSnackbar({ open: true, message: t('Link regeneration is disabled by company policy'), severity: 'error' });
      return;
    }

    if (!profile) {
      setSnackbar({ open: true, message: t('Please save your profile first'), severity: 'info' });
      return;
    }

    if (window.confirm(t('Are you sure you want to regenerate your public profile link? The old link will no longer work.'))) {
      try {
        const result = await regenerateSlug(profile.id);
        if (result.status === 200 && result.payload) {
          const saved = result.payload;
          setProfile(saved);
          setProfiles(prev => prev.map(p => p.id === saved.id ? saved : p));
          setFormData(prev => ({ ...prev, publicSlug: saved.slug }));
          setSnackbar({ open: true, message: t('Profile link regenerated successfully'), severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to regenerate link'), severity: 'error' });
        }
      } catch (err) {
        const msg = getApiErrorMessage(err, t, t('Failed to regenerate link'));
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    }
  }, [formData?.hrPolicies?.canRegenerateLink, profile, t]);

  const handleDownloadVCard = useCallback(() => {
    downloadVCard(
      formData?.firstName || currentUser.firstName,
      formData?.lastName || currentUser.lastName,
      formData?.jobTitle || currentUser.title,
      formData?.email || currentUser.email,
      formData?.phone || currentUser.phone,
      profileUrl
    );
  }, [formData, currentUser, profileUrl]);

  const handleAddAchievement = useCallback(async () => {
    if (!newAchievement?.title || !newAchievement?.date) return;

    if (profile) {
      try {
        const input: ProfileAchievementInput = {
          profileId: profile.id,
          title: newAchievement.title,
          description: newAchievement.description || undefined,
          date: newAchievement.date,
          displayOrder: formData.achievements.length
        };

        const result = await addAchievement(input);
        if (result.status === 200 && result.payload) {
          const newAch: Achievement = {
            id: result.payload.id,
            title: result.payload.title,
            date: result.payload.date || '',
            description: result.payload.description || '',
            displayOrder: result.payload.displayOrder
          };
          setFormData(prev => ({
            ...prev,
            achievements: [...prev.achievements, newAch]
          }));
          setNewAchievement({ title: '', date: '', description: '', displayOrder: 0 });
          setSnackbar({ open: true, message: t('Achievement added'), severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to add achievement'), severity: 'error' });
        }
      } catch (err) {
        const msg = getApiErrorMessage(err, t, t('Failed to add achievement'));
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        achievements: [...prev.achievements, { ...newAchievement, displayOrder: prev.achievements.length }]
      }));
      setNewAchievement({ title: '', date: '', description: '', displayOrder: 0 });
      setHasUnsavedChanges(true);
    }
  }, [newAchievement, profile, formData.achievements.length, t]);

  const handleEditAchievement = useCallback((index: number) => {
    setEditingAchievementIndex(index);
  }, []);

  const handleUpdateAchievement = useCallback((index: number, field: keyof Achievement, value: string) => {
    setFormData(prev => {
      const updatedAchievements = [...prev.achievements];
      updatedAchievements[index] = { ...updatedAchievements[index], [field]: value };
      return { ...prev, achievements: updatedAchievements };
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleSaveAchievementEdit = useCallback(async (index: number) => {
    const achievement = formData.achievements[index];
    if (profile && achievement.id) {
      try {
        const input: ProfileAchievementInput = {
          profileId: profile.id,
          title: achievement.title,
          description: achievement.description || undefined,
          date: achievement.date,
          displayOrder: achievement.displayOrder
        };

        const result = await updateAchievement(achievement.id, input);
        if (result.status === 200) {
          setSnackbar({ open: true, message: t('Achievement updated'), severity: 'success' });
          setHasUnsavedChanges(false);
        } else {
          setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to update achievement'), severity: 'error' });
        }
      } catch (err) {
        const msg = getApiErrorMessage(err, t, t('Failed to update achievement'));
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    }
    setEditingAchievementIndex(null);
  }, [formData.achievements, profile, t]);

  const handleDeleteAchievement = useCallback(async (index: number) => {
    if (!window.confirm(t('Are you sure you want to delete this achievement?'))) return;

    const achievement = formData.achievements[index];

    if (profile && achievement.id) {
      try {
        const result = await deleteAchievement(achievement.id);
        if (result.status === 200) {
          setFormData(prev => ({
            ...prev,
            achievements: prev.achievements.filter((_, i) => i !== index)
          }));
          setSnackbar({ open: true, message: t('Achievement deleted'), severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to delete achievement'), severity: 'error' });
        }
      } catch (err) {
        const msg = getApiErrorMessage(err, t, t('Failed to delete achievement'));
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        achievements: prev.achievements.filter((_, i) => i !== index)
      }));
      setHasUnsavedChanges(true);
    }
  }, [formData.achievements, profile, t]);

  const handleMoveAchievement = useCallback((index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData?.achievements?.length) return;

    setFormData(prev => {
      const updatedAchievements = [...prev.achievements];
      [updatedAchievements[index], updatedAchievements[newIndex]] = [updatedAchievements[newIndex], updatedAchievements[index]];
      updatedAchievements.forEach((a, i) => a.displayOrder = i);
      return { ...prev, achievements: updatedAchievements };
    });
    setHasUnsavedChanges(true);
  }, [formData?.achievements?.length]);

  // Social Link Handlers
  const handleAddSocialLink = useCallback(async (platform: string, url: string) => {
    if (!profile) {
      setSnackbar({ open: true, message: t('Please save your profile first'), severity: 'info' });
      return;
    }

    try {
      const input: ProfileSocialLinkInput = {
        profileId: profile.id,
        platform,
        url,
        displayOrder: formData.socialLinks.length
      };

      const result = await addSocialLink(input);
      if (result.status === 200 && result.payload) {
        setFormData(prev => ({
          ...prev,
          socialLinks: [...prev.socialLinks, {
            id: result.payload!.id,
            platform: result.payload!.platform,
            url: result.payload!.url,
            iconName: result.payload!.iconName,
            displayOrder: result.payload!.displayOrder
          }]
        }));
        setSnackbar({ open: true, message: t('Social link added'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to add social link'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to add social link'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }, [profile, formData.socialLinks.length, t]);

  const handleUpdateSocialLink = useCallback(async (index: number, link: SocialLink) => {
    if (!profile || !link.id) return;

    try {
      const input: ProfileSocialLinkInput = {
        profileId: profile.id,
        platform: link.platform,
        url: link.url,
        iconName: link.iconName,
        displayOrder: link.displayOrder
      };

      const result = await updateSocialLink(link.id, input);
      if (result.status === 200 && result.payload) {
        setFormData(prev => {
          const updatedLinks = [...prev.socialLinks];
          updatedLinks[index] = {
            id: result.payload!.id,
            platform: result.payload!.platform,
            url: result.payload!.url,
            iconName: result.payload!.iconName,
            displayOrder: result.payload!.displayOrder
          };
          return { ...prev, socialLinks: updatedLinks };
        });
        setSnackbar({ open: true, message: t('Social link updated'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to update social link'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to update social link'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }, [profile, t]);

  const handleDeleteSocialLink = useCallback(async (index: number) => {
    const link = formData.socialLinks[index];
    if (!link.id) return;

    try {
      const result = await deleteSocialLink(link.id);
      if (result.status === 200) {
        setFormData(prev => ({
          ...prev,
          socialLinks: prev.socialLinks.filter((_, i) => i !== index)
        }));
        setSnackbar({ open: true, message: t('Social link deleted'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to delete social link'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to delete social link'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }, [formData.socialLinks, t]);

  const handleMoveSocialLink = useCallback((index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData?.socialLinks?.length) return;

    setFormData(prev => {
      const updatedLinks = [...prev.socialLinks];
      [updatedLinks[index], updatedLinks[newIndex]] = [updatedLinks[newIndex], updatedLinks[index]];
      updatedLinks.forEach((l, i) => l.displayOrder = i);
      return { ...prev, socialLinks: updatedLinks };
    });
    setHasUnsavedChanges(true);
  }, [formData?.socialLinks?.length]);

  const isFieldAllowed = useCallback((field: string) => {
    return formData?.hrPolicies?.allowedFields?.includes(field) && !formData?.hrPolicies?.forbiddenFields?.includes(field);
  }, [formData?.hrPolicies]);

  const isFieldMandatory = useCallback((field: string) => {
    return formData?.hrPolicies?.mandatoryFields?.includes(field);
  }, [formData?.hrPolicies]);

  const validateForm = useCallback(() => {
    const newErrors: Errors = {};

    if (!formData?.bio?.trim() && isFieldMandatory('bio')) {
      newErrors.bio = t('Bio is required');
    }

    const emailOverride = formData?.emailOverride?.trim();
    if (emailOverride && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOverride)) {
      newErrors.emailOverride = t('Please enter a valid email address');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  }, [formData?.bio, formData?.emailOverride, isFieldMandatory, t]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;
    if (!selectedPersona) {
      setSnackbar({ open: true, message: t('Please select a persona'), severity: 'error' });
      return;
    }

    setIsSaving(true);

    try {
      const input: PublicProfileInput = {
        personaId: selectedPersona,
        bio: formData.bio || undefined,
        customJobTitle: formData.jobTitle || undefined,
        customLocation: formData.location || undefined,
        customCompanyName: formData.companyNameOverride || undefined,
        customEmail: formData.emailOverride || undefined,
        customPhone: formData.phoneOverride || undefined,
        showEmail: formData.showEmail,
        showPhone: formData.showPhone,
        showSkills: formData.showSkills,
        showAchievements: formData.showAchievements,
        colorScheme: formData.colorScheme || undefined,
        language: formData.profileLanguage || undefined
      };

      const result = profile
        ? await updateProfile(profile.id, input)
        : await createProfile(input);

      if (result.status === 200 && result.payload) {
        const saved = result.payload;
        setProfile(saved);
        setProfiles(prev => {
          const idx = prev.findIndex(p => p.id === saved.id);
          if (idx === -1) return [...prev, saved];
          const next = [...prev];
          next[idx] = saved;
          return next;
        });
        setFormData(prev => ({
          ...prev,
          publicSlug: saved.slug,
          profileStatus: saved.status?.toLowerCase() || 'draft',
          isPublic: saved.isPublic
        }));
        setHasUnsavedChanges(false);
        setSnackbar({ open: true, message: t('Profile saved successfully!'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.errors?.[0]?.description || t('Failed to save profile'), severity: 'error' });
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to save profile'));
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, selectedPersona, formData, profile, t]);

  const handlePreviewAsExternal = useCallback(() => {
    const slug = formData?.publicSlug;
    if (slug) {
      window.open(`/p/${slug}`, '_blank');
    } else {
      setSnackbar({ open: true, message: t('Please save your profile first to preview'), severity: 'info' });
    }
  }, [formData?.publicSlug, t]);

  const handleReset = useCallback(() => {
    if (window.confirm(t('Are you sure you want to reset all changes?'))) {
      setHasUnsavedChanges(false);
      fetchProfileData();
    }
  }, [fetchProfileData, t]);

  return {
    // State
    formData,
    profile,
    personas,
    contacts,
    selectedPersona,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    errors,
    snackbar,
    copiedToast,
    showShareMenu,
    showVisibilityAcknowledgment,
    editingAchievementIndex,
    activeTab,
    newAchievement,
    profileUrl,
    currentUser,
    selectedPersonaData,
    settingsStatus,
    isHrEditingMode,
    targetUserName,
    companyBranding,
    publicProfileDetail,

    // Actions
    setActiveTab,
    setShowShareMenu,
    setShowVisibilityAcknowledgment,
    setSnackbar,
    setNewAchievement,
    handleInputChange,
    handlePersonaChange,
    handleSave,
    handleReset,
    handlePublicToggle,
    handleAcknowledgePublic,
    handleCopyLink,
    handleRegenerateLink,
    handleDownloadVCard,
    handleAddAchievement,
    handleEditAchievement,
    handleUpdateAchievement,
    handleSaveAchievementEdit,
    handleDeleteAchievement,
    handleMoveAchievement,
    handlePreviewAsExternal,
    validateForm,
    isFieldAllowed,
    isFieldMandatory,
    // Social Links
    handleAddSocialLink,
    handleUpdateSocialLink,
    handleDeleteSocialLink,
    handleMoveSocialLink,
  };
}
